import dayjs from 'dayjs'
import { mapState } from 'vuex'

import { getLocalizationContext } from '@/helpers/translations'

const ContractCardMixin = {
  props: {
    contract: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      selectedPart: 0,
      chosenCurrency: { key: '', value: 1 },
      FORECASTED_CHART_LABELS: [this.$t('contract.stats.forecasted'), this.$t('contract.stats.original'), this.$t('contract.stats.adjustment')],
      ADJUSTED_CHART_LABELS: [this.$t('contract.stats.adjusted'), this.$t('contract.stats.original'), this.$t('contract.stats.adjustment')],
      showAdjusted: true,
      showVariations: false,
      currencyMenu: false
    }
  },
  computed: {
    ...mapState({
      variationsStages: state => state.variationsStages
    }),
    partsButtons () {
      const parts = [
        ...this.contract.parts.length > 1
          ? [{
              name: this.$t(this.partTranslationKey + '.all'),
              selected: true,
              currency: { },
              data: {
                original: {},
                forecastedPrice: ((this.contract.signedPriceCents + this.contract.forecastedPriceCents) / 100) / this.chosenCurrency.value,
                adjustedPrice: ((this.contract.signedPriceCents + this.contract.adjustedPriceCents) / 100) / this.chosenCurrency.value,
                currentPrice: (this.contract.currentPriceCents / 100) / this.chosenCurrency.value,
                signedPrice: (this.contract.signedPriceCents / 100) / this.chosenCurrency.value,
                adjustedCompletionTime: new Date(this.contract.adjustedCompletionTime),
                forecastedCompletionTime: new Date(this.contract.forecastedCompletionTime),
                variation: this.countVariations('variation'),
                claim: this.countVariations('claim')
              }
            }]
          : [],
        ...this.contract.parts.map((part, index) => {
          const currency = this.contract.currencies.find(i => i.key === part.currency)
          const signedPriceInMainCurrency = part.signedPriceCents * currency.value
          const forecastedPriceInMainCurrency = part.forecastedPriceCents * currency.value
          const adjustedPriceInMainCurrency = part.adjustedPriceCents * currency.value
          const currentPriceInMainCurrency = part.currentPriceCents * currency.value
          return {
            name: part.label || part.name,
            currency,
            id: part.id,
            data: {
              original: {
                signedPriceCents: part.signedPriceCents,
                forecastedPriceCents: part.forecastedPriceCents,
                adjustedPriceCents: part.adjustedPriceCents,
                currentPriceCents: part.currentPriceCents
              },
              forecastedPrice: ((signedPriceInMainCurrency + forecastedPriceInMainCurrency) / 100) / this.chosenCurrency.value,
              adjustedPrice: ((signedPriceInMainCurrency + adjustedPriceInMainCurrency) / 100) / this.chosenCurrency.value,
              currentPrice: (currentPriceInMainCurrency / 100) / this.chosenCurrency.value,
              signedPrice: (signedPriceInMainCurrency / 100) / this.chosenCurrency.value,
              adjustedCompletionTime: new Date(part.adjustedCompletionTime),
              forecastedCompletionTime: new Date(part.forecastedCompletionTime),
              variation: this.countVariations('variation', [part.id]),
              claim: this.countVariations('claim', [part.id])
            }
          }
        })
      ]
      const currencies = [...new Set((this.contract.parts || []).map(i => i.currency))].map(currency => {
        const selectedParts = parts.filter(i => i.currency && i.currency.key === currency)
        const partsIds = selectedParts.map(i => i.id)
        return {
          name: this.$t('contract.onlyCurrency', { currency }),
          data: {
            forecastedPrice: selectedParts.reduce((acc, part) => acc + part.data.original.signedPriceCents + part.data.original.forecastedPriceCents, 0) / 100,
            adjustedPrice: selectedParts.reduce((acc, part) => acc + part.data.original.signedPriceCents + part.data.original.adjustedPriceCents, 0) / 100,
            currentPrice: selectedParts.reduce((acc, part) => acc + part.data.original.currentPriceCents, 0) / 100,
            signedPrice: selectedParts.reduce((acc, part) => acc + part.data.original.signedPriceCents, 0) / 100,
            adjustedCompletionTime: new Date(Math.max(...selectedParts.map(i => new Date(i.data.adjustedCompletionTime)))),
            forecastedCompletionTime: new Date(Math.max(...selectedParts.map(i => new Date(i.data.forecastedCompletionTime)))),
            variation: this.countVariations('variation', partsIds),
            claim: this.countVariations('claim', partsIds)
          }
        }
      })
      return [...parts, ...(this.contract.isMultiCurrency ? currencies : [])]
    },
    currenciesButtons () {
    },
    adjustedBarsData () {
      return [
        {
          labels: this.ADJUSTED_CHART_LABELS,
          type: 'amount',
          label: this.$t('contract.stats.paid'),
          accepted: this.partsButtons[this.selectedPart].data.signedPrice,
          adjusted: this.partsButtons[this.selectedPart].data.adjustedPrice,
          current: this.partsButtons[this.selectedPart].data.currentPrice
        },
        {
          labels: this.ADJUSTED_CHART_LABELS,
          type: 'time',
          label: this.$t('contract.stats.time'),
          accepted: dayjs(this.contract.completionTime).diff(
            dayjs(this.contract.signatureDate),
            'day'
          ),
          adjusted: dayjs(this.partsButtons[this.selectedPart].data.adjustedCompletionTime).diff(
            dayjs(this.contract.signatureDate),
            'day'
          ),
          current: dayjs().diff(
            dayjs(this.contract.signatureDate),
            'day'
          )
        }
      ]
    },
    forecastedBarsData () {
      return [
        {
          labels: this.FORECASTED_CHART_LABELS,
          type: 'amount',
          label: this.$t('contract.stats.paid'),
          accepted: this.partsButtons[this.selectedPart].data.signedPrice,
          adjusted: this.partsButtons[this.selectedPart].data.forecastedPrice,
          current: this.partsButtons[this.selectedPart].data.currentPrice
        },
        {
          labels: this.FORECASTED_CHART_LABELS,
          type: 'time',
          label: this.$t('contract.stats.time'),
          accepted: dayjs(this.contract.completionTime).diff(
            dayjs(this.contract.signatureDate),
            'day'
          ),
          adjusted: dayjs(this.partsButtons[this.selectedPart].data.forecastedCompletionTime).diff(
            dayjs(this.contract.signatureDate),
            'day'
          ),
          current: dayjs().diff(
            dayjs(this.contract.signatureDate),
            'day'
          )
        }
      ]
    },
    pieChartData () {
      return [
        {
          headline: this.$t('contract.stats.variations'),
          data: this.partsButtons[this.selectedPart].data.variation
        },
        {
          headline: this.$t('contract.stats.claims'),
          data: this.partsButtons[this.selectedPart].data.claim
        }
      ]
    },
    partTranslationKey () {
      return getLocalizationContext(this.contract.isMultiCurrency)
    }
  },
  methods: {
    countVariations (type, partsIds) {
      const stages = this.variationsStages.variation || []
      return stages.map(({ name }, index) => {
        return {
          label: name,
          value: this.contract.stats
            .filter(i => i.type === type && i.stage === index && (partsIds ? (partsIds.includes(i.partId) || (i.partId === null && i.contractId === this.contract.id)) : true))
            .reduce((sum, curr) => sum + curr.count, 0)
        }
      })
    },
    async handleContractClick (contract) {
      this.$router.push({ name: 'contract', params: { contractId: contract.id } })
    }
  }
}

export default ContractCardMixin
